import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { normalizeMultiSelectValue } from './multiSelectUtils';
import { filterOptionsByQuery } from '../../../utils/selectOptions';

const MENU_MAX_HEIGHT = 240;
const VIEWPORT_PADDING = 8;
const GAP = 4;

/**
 * Collapsible multi-select dropdown for filter fields.
 * Menu is portaled to document.body so it is not clipped by overflow:hidden parents.
 * Options are A–Z sorted with type-to-search.
 */
const OptionsPanelField = ({
  field,
  value,
  onChange,
  onFocus,
  loading,
  isLoading,
}) => {
  const { name, label } = field;
  const options = field.options || [];
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  const selected = normalizeMultiSelectValue(value);

  const sortedOptions = useMemo(() => {
    return [...options].sort((a, b) =>
      String(a.label || '').localeCompare(String(b.label || ''), undefined, {
        sensitivity: 'base',
        numeric: true,
      })
    );
  }, [options]);

  const filteredOptions = useMemo(
    () =>
      filterOptionsByQuery(
        sortedOptions.map((o, index) => ({
          value: String(o.value),
          label: String(o.label || o.value),
          index,
          raw: o,
        })),
        query
      ),
    [sortedOptions, query]
  );

  const computeMenuStyle = useCallback(() => {
    const trigger = rootRef.current;
    if (!trigger) return null;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    const spaceAbove = rect.top - VIEWPORT_PADDING;
    const shouldOpenUp = spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
    const maxHeight = Math.min(
      MENU_MAX_HEIGHT,
      Math.max(120, shouldOpenUp ? spaceAbove - GAP : spaceBelow - GAP)
    );

    return {
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      maxHeight,
      zIndex: 99999,
      ...(shouldOpenUp
        ? { bottom: window.innerHeight - rect.top + GAP }
        : { top: rect.top + rect.height + GAP }),
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null);
      return;
    }
    setMenuStyle(computeMenuStyle());
  }, [open, computeMenuStyle, options.length, isLoading]);

  useEffect(() => {
    if (!open) return undefined;

    const handleReposition = () => setMenuStyle(computeMenuStyle());
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [open, computeMenuStyle]);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      const inTrigger = rootRef.current?.contains(event.target);
      const inMenu = menuRef.current?.contains(event.target);
      if (!inTrigger && !inMenu) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  useEffect(() => {
    if (open && onFocus) {
      onFocus();
    }
  }, [open, onFocus]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    setTimeout(() => searchRef.current?.focus(), 0);
  }, [open]);

  const headerHint = () => {
    if (selected.length > 0) {
      return `${selected.length} selected`;
    }
    return 'Any';
  };

  const toggleOption = (optionValue) => {
    const strVal = String(optionValue);
    const next = selected.includes(strVal)
      ? selected.filter((v) => v !== strVal)
      : [...selected, strVal];
    onChange({ target: { name, value: next } });
  };

  const clearSelection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onChange({ target: { name, value: [] } });
  };

  const toggleOpen = () => {
    if (loading) return;
    setOpen((prev) => !prev);
  };

  const menu =
    open && menuStyle
      ? createPortal(
          <div
            ref={menuRef}
            className="border rounded shadow-lg flex flex-col"
            style={{
              ...menuStyle,
              backgroundColor: 'var(--color-surface, #fff)',
            }}
            role="listbox"
            aria-multiselectable="true"
          >
            <div className="p-2 border-b shrink-0">
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to search…"
                className="w-full px-2 py-1.5 text-sm border rounded"
                style={{
                  backgroundColor: 'var(--color-surface, #fff)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text)',
                }}
                autoComplete="off"
              />
            </div>
            <div className="overflow-y-auto p-2 space-y-1 flex-1 min-h-0">
              {isLoading ? (
                <div className="text-xs opacity-70 px-1 py-2">Loading options…</div>
              ) : sortedOptions.length === 0 ? (
                <div className="text-xs opacity-70 px-1 py-2">No options yet</div>
              ) : filteredOptions.length === 0 ? (
                <div className="text-xs opacity-70 px-1 py-2">No matches</div>
              ) : (
                filteredOptions.map((option) => {
                  const optVal = option.value;
                  const checked = selected.includes(optVal);
                  return (
                    <label
                      key={`${optVal}-${option.index}`}
                      className="flex items-center gap-2 text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-1 py-0.5"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={loading || isLoading}
                        onChange={() => toggleOption(optVal)}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })
              )}
            </div>
            {selected.length > 0 && (
              <div className="px-2 pb-2 border-t pt-2 shrink-0">
                <button
                  type="button"
                  disabled={loading || isLoading}
                  onClick={clearSelection}
                  className="text-xs text-blue-600 hover:underline disabled:opacity-50"
                >
                  Clear selection
                </button>
              </div>
            )}
          </div>,
          document.body
        )
      : null;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={toggleOpen}
        disabled={loading}
        aria-expanded={open}
        aria-haspopup="listbox"
        className="w-full px-3 py-2 border rounded text-sm flex items-center justify-between gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-left"
        style={{ backgroundColor: 'var(--color-surface, #fff)' }}
      >
        <span className="font-medium truncate" title={label}>
          {label}
        </span>
        <span className="flex items-center gap-1 shrink-0 text-xs opacity-70">
          <span>{headerHint()}</span>
          <span aria-hidden="true">{open ? '▲' : '▼'}</span>
        </span>
      </button>
      {menu}
    </div>
  );
};

export default OptionsPanelField;
